/* App.js ─────────────────────────────────────────────────────────────── */
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { ReactTyped } from 'react-typed';
import './App.css';
import ArtDeck,{ artData, scaledHeight } from './components/ArtDeck';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import RoomSlots from './components/RoomSlots';
import { AnimatePresence, motion } from 'framer-motion';
import 'keyboard-css/dist/css/main.min.css';
import curatedMap from './components/curatedLookup';

/* ─────────── SCALE SETTINGS ───────────
   Change these two numbers to whatever resolution
   you originally laid out the room at.               */
const BASE_W = 1920;   // design-width  px
const BASE_H = 1080;    // design-height px

/* ─────────── CONFIG (unchanged) ─────────── */
const DEV = !process.env.NODE_ENV || process.env.NODE_ENV === 'development';

const searchEndpoint = DEV
  ? (q) =>
      `https://www.googleapis.com/youtube/v3/search` +
      `?part=snippet&type=video&videoEmbeddable=true&videoSyndicated=true` +
      `&maxResults=5&q=${encodeURIComponent(q)}` +
      `&key=${process.env.REACT_APP_YT_KEY}`
  : (q) => `/api/youtubeSearch?q=${encodeURIComponent(q)}`;

/* ─────────── QUIZ DATA (unchanged) ─────────── */
const personalityQuestions = [
  {
    id: "party_type",
    text: "You are hosting a party — what kind?",
    options: ["INTIMATE HANG", "FULL HOUSE"]
  },
  {
    id: "priority",
    text: "What’s more important?",
    options: ["A STANDOUT PIECE", "A SEAMLESS VIBE"]
  },
  {
    id: "feeling",
    text: "Your room should feel like…",
    options: ["A MIRROR", "AN ESCAPE"]
  }
];

const questions = [
  {
    id: 'style',
    text: 'Choose a bedroom style.',
    options: [
      { label: 'BOHEMIAN', key: 'bohemian' },
      { label: 'ECLECTIC', key: 'eclectic' },
      { label: 'MID-CENTURY MODERN', key: 'mid-century modern' },
      { label: 'MINIMALIST', key: 'minimalist' },
    ],
  },
  {
    id: 'color',
    text: 'Choose a color palette.',
    options: [
      { label: 'NEUTRAL', key: 'neutral' },
      { label: 'HAZY', key: 'hazy' },
      { label: 'BRIGHT', key: 'bright' },
      { label: 'DARK', key: 'dark' },
      { label: 'SIMPLE', key: 'simple'},
    ],
  },
];

/* ─────────── COMPONENT ─────────── */
export default function App() {
  /* quiz state */
  const [stage, setStage] = useState('intro');
  const [line,  setLine]  = useState(1);
  const [ans,   setAns]   = useState({ style: '', color: '' });
  const [inputFocused,   setInputFocused]   = useState(false);
  const [hoverDropdown,  setHoverDropdown]  = useState(false);
  const searchWrapRef = useRef(null);
  const [deckCollapsed, setDeckCollapsed] = useState(false);
  const [showDeckPrompt, setShowDeckPrompt] = useState(true);
  const [showCuratePrompt, setShowCuratePrompt] = useState(true);
  const [personalityAnswers, setPersonalityAnswers] = useState({});
  const [deck, setDeck] = useState([]);
  const [wasArtDropped, setWasArtDropped] = useState(false);

  const normalize = (str) => {
    if (!str) return '';
    return str.trim().toLowerCase().replace(/^(a |an )/i, '');
  };


  const [scale, setScale] = useState(1);
  const [shouldScale, setShouldScale] = useState(false);
  const [designAnswers, setDesignAnswers] = useState({
    style: null,
    color: null,
  });
  const roomKey = React.useMemo(() => {
    return `${normalize(personalityAnswers.party_type)}_${normalize(personalityAnswers.priority)}_${normalize(personalityAnswers.feeling)}_${normalize(ans.style)}_${normalize(ans.color)}`;
  }, [
    personalityAnswers.party_type,
    personalityAnswers.priority,
    personalityAnswers.feeling,
    ans.style,
    ans.color,
  ]);

  const prevKeyRef = useRef(null)

  useEffect(() => {
    const updateScale = () => {
      const widthRatio = window.innerWidth / BASE_W;
      const heightRatio = window.innerHeight / BASE_H;

      const shouldScaleNow = window.innerWidth < BASE_W && window.innerHeight < BASE_H;
      setShouldScale(shouldScaleNow);
      setScale(Math.min(widthRatio, heightRatio));
    };

    window.addEventListener('resize', updateScale);
    updateScale();

    return () => window.removeEventListener('resize', updateScale);
  }, []);

  // Combine both sets of questions into one ordered array of stages
  const allStages = [
    ...personalityQuestions.map(q => q.id),
    ...questions.map(q => q.id)
  ];

  // Determine which stage you're on
  const idx = allStages.findIndex((q) => q === stage);
  const isPersonalityStage = personalityQuestions.some((q) => q.id === stage);
  const isDesignStage = questions.some((q) => q.id === stage);

  // Get the current question object
  const qCur = isPersonalityStage
    ? personalityQuestions.find((q) => q.id === stage)
    : questions.find((q) => q.id === stage);

  // Handle answers based on which type of question it is
  const answer = (id, val) => {
    if (isPersonalityStage) {
      setPersonalityAnswers(prev => ({ ...prev, [id]: val }));
    } else {
      const newAns = { ...ans, [id]: val };
      setAns(newAns);
      if (id === 'style') {
        setAns({ style: val, color: '' });  // Clear color if style changes
      }
    }

    const next = allStages[idx + 1];
    if (next) {
      setStage(next);
    } else {
      setStage('complete');
    }
  };


  /* YouTube search / playback */
  const [query, setQuery]     = useState('');
  const [list,  setList]      = useState([]);
  const [vid,   setVid]       = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  const playerRef = useRef(null);

  /* simple in-memory cache for search */
  const cache = useRef({});

  /* close dropdown if a click lands outside card + list */
  useEffect(() => {
    if (!(inputFocused || hoverDropdown)) return;        // nothing open → no listener
    const onDocClick = (e) => {
      if (!searchWrapRef.current?.contains(e.target)) {
        setInputFocused(false);
        setHoverDropdown(false);
        setList([]);   // clear suggestions
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [inputFocused, hoverDropdown, setList]);

  useEffect(() => {
    if (stage === 'complete') {
      setShowCuratePrompt(true);
      setShowDeckPrompt(true);
    }
  }, [stage]);

  /* fetch YouTube */
  const fetchYT = useCallback(async (q) => {
    if (q.length < 3) return [];
    if (cache.current[q]) return cache.current[q];

    const res = await fetch(searchEndpoint(q));
    if (!res.ok) return [];
    const data = await res.json();
    cache.current[q] = data.items;
    return data.items;
  }, []);
  
  useEffect(() => {
    const id = setTimeout(() => fetchYT(query).then(setList), 400);
    return () => clearTimeout(id);
  }, [query, fetchYT]);

  /* mount / swap player */
  useEffect(() => {
    if (!vid) return;
    if (playerRef.current?.destroy) {
      playerRef.current.destroy();
      playerRef.current = null;
    }
    const p = new window.YT.Player('yt-player', {
      videoId: vid,
      playerVars: { autoplay: 1, controls: 0 },
      events: {
        onReady: () => {
          playerRef.current = p;
          setIsPlaying(true);
        },
      },
    });
  }, [vid]);

  const handleCurate = () => {
    const shuffled = [...deck].sort(() => 0.5 - Math.random());
    const [slotA, slotB, slotC] = shuffled.slice(0, 3);

    setSlots({ slotA, slotB, slotC });
    setShowCuratePrompt(false);
  };
    
  /* visuals */
  const bgImg = useMemo(() => {
  if (ans.style) {
    const styleKey = ans.style.replace(/\s+/g, '-').toLowerCase();
    // If color is "simple", don't change background — keep same as previous
      if (ans.color === 'simple') {
        return `/bg_${styleKey}_nocolor.png`;
      }
    const colorKey = ans.color ? ans.color.toLowerCase() : 'nocolor';
    return `/bg_${styleKey}_${colorKey}.png`;
  } else {
    return process.env.PUBLIC_URL + '/empty.png';
  }
}, [ans.style, ans.color]);

  const [slots, setSlots] = useState({
    slotA: null,
    slotB: null,
    slotC: null,
  });

  useEffect(() => {
    if (stage !== 'complete') return;

    if (roomKey && roomKey !== prevKeyRef.current) {
      const curatedIds = curatedMap[roomKey];

      if (!curatedIds || curatedIds.length !== 8) {
        console.warn('❗ No curated deck for key:', roomKey);
        setDeck([]); // or fallback if you want
        prevKeyRef.current = roomKey;
        return;
      }

      const curatedDeck = curatedIds
        .map(id => artData.find(p => p.id === id))
        .filter(Boolean);

      // only shuffle ORDER, never the contents
      const shuffledOnce = [...curatedDeck].sort(() => Math.random() - 0.5);

      setDeck(shuffledOnce);
      prevKeyRef.current = roomKey;
    }
  }, [stage, roomKey]);


  useEffect(() => {
    if (!showDeckPrompt || !wasArtDropped) return;

    if (Object.values(slots).some(v => v !== null)) {
      setShowDeckPrompt(false);
    }
  }, [slots, showDeckPrompt, wasArtDropped]);

  useEffect(() => {
    console.log('Stage:', stage);
  }, [stage]);

  useEffect(() => {
    const designEntryStage = 'color'; // or whatever your first design stage ID is
    if (stage === designEntryStage) {
      setQuery('');
      setVid(null);
      setIsPlaying(false);
      setHasPlayed(false);
      setSlots({
        slotA: null,
        slotB: null,
        slotC: null,
      });
      
    }
  }, [stage]);

  const [bgLoaded, setBgLoaded] = useState(false);
  useEffect(() => {
    setBgLoaded(false);  // Reset loading state when bgImg changes

    const img = new Image();
    img.src = bgImg;
    img.onload = () => setBgLoaded(true);
  }, [bgImg]);

  const [hoveredId, setHoveredId] = useState(null);

  /* ─────────── JSX ─────────── */
  return (
    <DndProvider backend={HTML5Backend}>
      <AnimatePresence mode="wait">
        {bgLoaded && (
          <motion.div
            key={bgImg}  // triggers animation on change
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            style={{
              position: 'fixed',
              inset: 0,
              background: `url(${bgImg}) center / cover no-repeat`,
              zIndex: -1,
              width: '100vw',
              height: '100vh',
            }}
          />
        )}
      </AnimatePresence>
      <div
        style={{
          position: 'fixed',
          top: '40%',
          left: '50%',
          width: BASE_W,
          height: BASE_H,
          transform: `translate(-50%, -50%) scale(${scale})`,
          transformOrigin: 'center',
          overflow: 'hidden',
        }}
      >
          {/* intro + personality quiz*/}
          {(stage === 'intro' || stage === 'ready') && (
            <div
              className="typed-section"
              style={{
                position: 'fixed',
                width: 'max-content',
                top: '45%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',      
              }}
            >
              <div className="typed-line">
                {line >= 1 && (
                  <ReactTyped
                    strings={['Build your virtual room.']}
                    typeSpeed={20}
                    showCursor={false}
                    onComplete={() => setTimeout(() => setLine(2), 600)}
                  />
                )}
              </div>
              <div className="typed-line">
                {line >= 2 && (
                  <ReactTyped
                    strings={['Perfect your space with curated CRASHPAD art pieces.']}
                    typeSpeed={20}
                    showCursor={false}
                    onComplete={() => setTimeout(() => setLine(3), 600)}
                  />
                )}
              </div>
              <div className="typed-line">
                {line >= 3 && (
                  <ReactTyped
                    strings={['Add your top picks to your cart to bring it home.']}
                    typeSpeed={20}
                    showCursor={false}
                    onComplete={() => setTimeout(() => setStage('ready'), 600)}
                  />
                )}
              </div>
              <button
                className={`primary-button big-button ${
                  stage === 'ready' ? 'visible' : 'invisible'
                }`}
                onClick={() => setStage(personalityQuestions[0].id)}
              >
                LET&#39;S&nbsp;GO
              </button>
            </div>
          )}  

          {/* quiz */}
          {qCur && (
            <>
              {/* Personality Questions – Moodboard Style */}
              {isPersonalityStage && (
                <div
                  style={{
                    position: 'fixed',
                    width: 1200,
                    height: 500,
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 999
                  }}
                >
                  <div
                    className="fade-in"
                    style={{
                      background: 'white',
                      borderRadius: '0px',
                      padding: '4rem',
                      width: 'min(300vw, 700px)',
                      boxShadow: '0 0 20px rgba(0,0,0,0.1)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '1.5rem',
                      alignItems: 'center',
                      position: 'relative'
                    }}
                  >
                    {/* Progress Bar */}
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      height: '4px',
                      width: `${((idx + 1) / personalityQuestions.length) * 100}%`,
                      backgroundColor: '#ffe400',
                      borderRadius: '0 10px 10px 0',
                      transition: 'width 0.4s ease-in-out'
                    }} />

                    {/* Question Text */}
                    <h2 style={{ margin: 0, flexWrap: 'nowrap', fontSize: 'clamp(40px, 1vw + 5px, 60px)', textAlign: 'center' }}>
                      {qCur.text}
                    </h2>

                    {/* Two Answer Options */}
                    <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'nowrap', fontSize: 'clamp(30px, 1vw + 5px, 50px)', justifyContent: 'center' }}>
                      {qCur.options.map((opt, i) => (
                        <button
                          key={i}
                          className="personality-button"
                          onClick={() => answer(qCur.id, opt)}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* complete stage */}
          {stage === 'complete' && (
            <>
              {/* ---- wall slots ---- */}
              <div className="fade-in"
                style={{
                  position:'fixed',
                  top: 200,
                  left: 450,
                  pointerEvents: 'auto',
                  zIndex:1000,
                }}
              >
                <RoomSlots 
                    slots={slots} 
                    setSlots={setSlots} 
                    setShowDeckPrompt={setShowDeckPrompt}
                    setWasArtDropped={setWasArtDropped}
                /> 
              </div>
          </>
        )}
      </div>
    {/* Design Questions */}
    {isDesignStage && (
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: '50%',
          transform: `translateX(-50%) scale(${scale})`,
          transformOrigin: 'top center',
          zIndex: 3000,
        }}
      >
        <div
          className="question-block"
          style={{
            width: BASE_W,
            paddingTop: '100px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            rowGap: '.6rem',
            borderRadius: '10px',
          }}
        >
        <h1 className="fade-in" style={{ fontSize: 'clamp(55px, 1vw + 8px, 60px)', margin: 0 }}>{qCur.text}</h1>
          <div style={{ display: 'flex', textAlign: 'center', gap: '1rem', flexWrap: 'nowrap', justifyContent: 'center' }}>
            {qCur.options.map((o) => (
              <button key={o.key}
                className={`primary-button fade-in" ${o.key === 'simple' ? 'simple-style' : ''}`}
                
                style={{fontSize: 'clamp(30px, 1vw + 8px, 40px)' }}
                onClick={() => answer(qCur.id, o.key)}>
                {o.label}
              </button>
            ))}
          </div>
          {/* BACK BUTTON → returns to beginning of personality */}
          {isDesignStage && (
            <button
              className="secondary-button"
              onClick={() => {
                const designIdx = questions.findIndex((q) => q.id === stage);
                if (designIdx === 0) {
                  // First design question → go back to last personality question
                  setStage(personalityQuestions[personalityQuestions.length - 3].id);
                } else {
                  const prevStage = questions[designIdx - 1].id;
                  setStage(prevStage);

                  // If going back to 'style', reset style and color
                  if (prevStage === 'style') {
                    setAns({ style: '', color: '' });

                    setDeck([]);
                    prevKeyRef.current = null;
                  }
                }
              }}
            >
              ←
            </button>
          )}
        </div>
      </div>
    )}
    {/* complete stage */}
    {stage === 'complete' && (  
    <>
      <div 
          style={{
            position: 'fixed',
            top: 10,
            left: '50%',
            transform: `translateX(-50%) scale(${scale})`,
            transformOrigin: 'top center',
            zIndex: 3000,
          }}
        >
          {/* control card */}
          <div
            ref={searchWrapRef}
            style={{
              zIndex:2000,
              pointerEvents:'auto',
              top: hasPlayed ? 16 : 20,
              background: 'rgba(255,255,255,0)',
              padding: hasPlayed ? '0' : '.2rem 1rem',
              borderRadius: '2px',

              /* layout changes after first song */
              display: 'flex',
              flexDirection: hasPlayed ? 'row' : 'column',
              alignItems: 'center',
              columnGap: hasPlayed ? 12 : 0,
              rowGap: hasPlayed ? 0 : 6,
            }}
          >
            {/* play / pause toggle */}
            <button
              className={`toggle-button ${isPlaying ? 'playing' : ''}`}
              disabled={!vid}
              onClick={() => {
                if (!playerRef.current) return;
                const st = playerRef.current.getPlayerState?.(); // 1 = playing
                if (st === 1) { playerRef.current.pauseVideo(); setIsPlaying(false); }
                else          { playerRef.current.playVideo();  setIsPlaying(true);  }
              }}
              style={{
                fontSize: 23,
                border: 'none',
                cursor: vid ? 'pointer' : 'default',
              }}
            >⏯</button>

            {/* prompt disappears after first play */}
            {!hasPlayed && (
                <ReactTyped
                  strings={['Play your favorite song to set the mood...']}
                  typeSpeed={30}
                  showCursor={true}
                  onComplete={() => setTimeout(() => setLine(4), 600)}
                  style={{
                    fontStyle:'italic',
                    textAlign: 'center',
                    flexWrap: 'nowrap',
                    fontSize: 'clamp(20px, 1vw + 5.5px, 25px)',
                    color: 'black',
                    backgroundColor: '#ffe400',
                    padding: '0.3em 0.3rem',
                  }}
                />
            )}
            {/* search box and dropdown together */}
            <div
              style={{
                position: 'relative',
                width: hasPlayed ? 400 : '100%',
                boxShadow: '0 0px 8px rgba(0,0,0,0.1)',
              }}
            >
              <input
                className="search-input fade-in"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => setInputFocused(true)}
                onBlur={() => setTimeout(() => setInputFocused(false), 100)}
                placeholder="Search YouTube…"
                style={{
                  width: '96%',
                  padding: '0.8rem 0.5rem',
                  borderRadius: '18px',
                  border: '1px solid #ccc',
                  fontFamily: 'SFMono-Regular, Menlo, Monaco, monospace',
                  fontSize: '1rem',
                }}
              />

              {/* dropdown floats under input */}
              {list.length > 0 && (inputFocused || hoverDropdown) && (
                <ul
                  onMouseEnter={() => setHoverDropdown(true)}
                  onMouseLeave={() => setHoverDropdown(false)}
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    width: '96%',
                    margin: '0 .5rem',
                    padding: '0rem 0',
                    listStyle: 'none',
                    background: 'rgba(255, 255, 255, 0.41)',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    borderRadius: '3px',
                    zIndex: 2500,
                    maxHeight: '280px',
                    overflowY: 'auto',
                    pointerEvents: 'auto',
                  }}
                >
                  {list.map((item) => (
                    <li
                      key={item.id.videoId}
                      onClick={() => {
                        setVid(item.id.videoId);
                        setList([]);
                        setHasPlayed(true);
                        setInputFocused(false);
                        setHoverDropdown(false);
                      }}
                      onMouseEnter={() => setHoveredId(item.id.videoId)}
                      onMouseLeave={() => setHoveredId(null)}
                      style={{ padding: '0.5rem 1rem', cursor: 'pointer', 
                        backgroundColor: hoveredId === item.id.videoId ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
                      color: hoveredId === item.id.videoId ? '#000' : '#111',
                      transition: 'background-color 0.2s ease',
                      }}
                    >
                      {item.snippet.title} — {item.snippet.channelTitle}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {/* back arrow moves inline */}
            <button
              className="secondary-button"
              style={{ marginLeft: hasPlayed ? 0: 0, marginTop: hasPlayed ? 0 : '0rem' }}
              onClick={() => {
                setAns(prev => ({ ...prev, color: '' }));
                setStage('color');
                setVid(null);
                setIsPlaying(false);
                setHasPlayed(false);
                setShowCuratePrompt(true);
                setShowDeckPrompt(true);
                setWasArtDropped(false);
                setDeck([]);
                prevKeyRef.current = null;
              }}
            >←</button>
          </div>
        </div>
    </>
    )}
    {/* Deck overlay */}
    {stage === 'complete' && (
    <div 
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        width: '100vw',
        margin:0,
        padding:0,
        zIndex: 1000,
        display:'flex',
        flexDirection:'column',
        alignItems:'flex-start'
      }}
    > 
        {/* CURATE button */} 
        <button className="kbc-button kbc-button-md"
        onClick={handleCurate}
        style={{  
          position: 'fixed',
          top: 6,
          right:'1%',
          fontSize: 'clamp(.3rem, 1.3vw, 1.25rem)',
          fontFamily: 'SFMono-Regular, Menlo, Monaco, monospace',
          color: '#545454',
          fontWeight: '400',
          cursor: 'pointer',
          padding: 'clamp(0.3rem, .5vh, 0.6rem) clamp(0.2rem, 2vw, 1rem)',
          /* === CORE PALETTE === */
          '--kbc-key-bg':         'hsl(40, 100%, 94%)',  /* cap face */
          '--kbc-key-shadow':     'hsl(44, 40%, 83%)',  /* first bevel */
          '--kbc-key-shadow2':    'hsl(45, 38%, 74%)',  /* deeper base */

          /* === ACTIVE (pressed) STATE) ===
            these will be applied automatically on :active */
          '--kbc-key-active-bg':      'hsl(45, 38%, 74%)',  /* cap when pressed */
          '--kbc-key-active-shadow':  'hsl(45, 38%, 74%)',  /* bevel when pressed */
          '--kbc-key-active-shadow2': 'hsl(40, 35%, 65%)',  /* base when pressed */
        }}
      >curate
      </button>
          {/*prompt */}
          {showCuratePrompt && (
            <ReactTyped
              strings={['Press to rapid-test wall art sets.']}
              typeSpeed={30}
              showCursor={true}
              style={{
                position: 'fixed',
                right: '1%', 
                top: 'clamp(40px, 8.5%, 65px)',
                fontStyle:'italic',
                fontSize: 'clamp(7px, 1vw + 1px, 20px)',
                color: 'black',
                backgroundColor: '#ffe400',
                padding: '0.3em 0.5em',
              }}
            />
          )}

      {/*prompt */}
      {showDeckPrompt && !deckCollapsed && ( 
        <ReactTyped
          strings={['Drag &amp; drop from your curated gallery of CRASHPAD art pieces.']}
          typeSpeed={30}
          showCursor={true}
          onComplete={() => setTimeout(() => setLine(2), 600)}
          style={{
            fontStyle:'italic',
            fontSize: 'clamp(5px, 1vw + 1px, 20px)',
            color: 'black',
            backgroundColor: '#ffe400',
            padding: '0.3em 0.1em',
            marginLeft: '0em',
            alignSelf: 'flex-start',
          }}
        />
      )}
      
      {/* Deck row with collapse button */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-end',
          padding:0,
          margin:0,
          width:'100%',
        }}
      >
        <AnimatePresence mode="wait">
          {!deckCollapsed && <ArtDeck deck={deck}/>}
        </AnimatePresence>
        
        {/* Collapse/Expand button */}
        <motion.div
          animate={{
            x: deckCollapsed ? 0 : `77.6vw`, // deck width in vw
          }}
          initial={false}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            height: scaledHeight,
          }}
        >
          <button
            className="collapse-button"
            style={{  
              width: '18px',
              height: '100%',
              background: '#ffe400',
              color: 'black',
              font: 'ExposureTrial',
              fontSize: '1em',
              fontFamily: 'InstrumentSans',
              fontWeight: '600',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'center',
              alignItems:'center',
            }}
            onClick={() => setDeckCollapsed(c => !c)}
          >
            {deckCollapsed ? '→' : '←'}
          </button>
        </motion.div>
      </div>
    </div>)}

    {/* laptop */}
    {stage === 'complete' && (
      <>
        {/* laptop overlay */}
          <div
            style={{
              position: 'fixed',
              top:      'clamp(20px, 57vh, 700px)',            // ← tweak these 4 values
              left:     'clamp(50px, 48vw, 1000px)',           //     until the laptop is where you want
              width:    'clamp(50px, 17vh, 180px)',            // 
              height:   'auto',            //
              pointerEvents:'auto', 
            }}
          >
            {/* have the image fill the wrapper */}
            <img
              src="/laptop1.png"
              alt="laptop1"
              style={{ width: '100%', height: '100%' }}
            />

            {/* then position the YT-player relatively inside it */}
            <div
              style={{
                position:      'absolute',
                top:'28.1%', left:'19%', width:'62.5%',     height:'30%',      
                borderRadius:  '1px',
                overflow:      'hidden',
                pointerEvents: 'auto',
              }}
            >
              <div
                id="yt-player"
                style={{ width: '100%', height: '100%' }}/>
            </div>
          </div>
        </>
    )}
  </DndProvider>
  );
}
